import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import CellTowerIcon from "@mui/icons-material/CellTower";
import HomeIcon from "@mui/icons-material/Home";
import ListItemText from "@mui/material/ListItemText";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LogoutIcon from "@mui/icons-material/Logout";
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import AccountInfo from "./AccountInfo";
import { Link as RouterLink } from "react-router-dom";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import { AppRole, getAppRole } from "../utils/AppRole";
import { useLocation } from "react-router-dom"; // Import useLocation

const drawerWidth = 240;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

type Props = {
  children?: React.ReactNode;
};

export const PageLayout: React.FC<Props> = ({ children }) => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const { instance } = useMsal();
  //const [role, setRole] = React.useState(AppRole.Guest);

  const { result, error } = useMsalAuthentication(InteractionType.Redirect, {
    ...loginRequest,
    redirectUri: "/",
  });

  const role = getAppRole(result?.accessToken);

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const menuOptions = [
    { label: "Home", route: "/", isAdmin: false, icon: HomeIcon },
    { label: "Home", route: "/", isAdmin: true, icon: HomeIcon },
    {
      label: "Part 1 Form",
      route: "/form-one",
      isAdmin: false,
      icon: ListAltIcon,
    },
    {
      label: "Part 4 Form",
      route: "/form-four",
      isAdmin: false,
      icon: ListAltIcon,
    },
    { label: "NRUF", route: "/upload", isAdmin: false, icon: FileUploadIcon },
    { label: "NRUF", route: "/files", isAdmin: true, icon: FileOpenIcon },
    {
      label: "Carriers",
      route: "/carriers",
      isAdmin: true,
      icon: CellTowerIcon,
    },
    {
      label: "Part 1 Forms",
      route: "/admin-form-one",
      isAdmin: true,
      icon: ListAltIcon,
    },
    {
      label: "Part 1B Forms",
      route: "/admin-form-one-b",
      isAdmin: true,
      icon: ListAltIcon,
    },
    {
      label: "Part 4 Forms",
      route: "/admin-form-four",
      isAdmin: true,
      icon: ListAltIcon,
    },
    {
      label: "Part 5 Forms",
      route: "/admin-form-five",
      isAdmin: true,
      icon: ListAltIcon,
    },
  ];

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>
          <img alt="CNA Logo" title="CNA" src="https://www.cnac.ca/images/cna_logo_transparent_60x60.png"></img>
          <Typography variant="h5" noWrap component="div">
            Canadian Numbering Administrator Secure Portal
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <AccountInfo />
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {menuOptions
            .filter((i) =>
              role === AppRole.Administrator
                ? i.isAdmin === true
                : i.isAdmin === false
            )
            .map((item, index) => (
              <ListItem key={item.label}
                disablePadding
                selected={location.pathname === item.route}>
                <ListItemButton component={RouterLink} to={item.route}>
                  <ListItemIcon>
                    {item.icon ? <item.icon /> : <FileOpenIcon />}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
        <Divider />
        <List>
          <ListItem key="Logout" disablePadding>
            <ListItemButton onClick={() => handleLogout()}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
};
